import { FormBuilder, FormGroup } from '@angular/forms';
import { ReactiveFormsValidator } from './forms-validator';
import { ToastyService } from 'ng2-toasty';
import { AgentRegister } from '../models/agents/agent-register';
import { RegistratinEntityType } from '../models/enums/registration-entity-type.enum';
import { PersonType } from '../models/enums/person-type.enum';
import { WizardCompanyEnum } from '../models/enums/wizard-company.enum';
import { AgentRoutes } from './control-agent-routes';
import { AgentType } from '../models/enums/agent-type-enum';
import { CompanyService } from '../services/company.service';
import { Select } from '@ngxs/store';
import { CompanyState } from '../state-manager/state/company-register.state';
import { Observable } from 'rxjs';
import { ServicesTypes } from '../models/enums/services.enum';
import { ServiceRelationship } from '../models/agents/service-relationship';

export abstract class ControlRegisterSections extends ReactiveFormsValidator {
  @Select(CompanyState.dispensedSuitability)
  dispensedSuitability$: Observable<boolean>;
  private dispensedSuitability: boolean;
  private personSectionInvestorDataDefault: RegisterSection;
  private personSectionAuthorizationsDataDefault: RegisterSection;
  private personSectionContractDefault: RegisterSection;
  private personSectionProfesionDataDefault: RegisterSection;
  private personSectionFinancialDefault: RegisterSection;
  public abstract formGroup: FormGroup;
  public abstract initReactiveForms(): void;

  constructor(
    protected formBuilder: FormBuilder,
    protected toastyService: ToastyService,
    protected companyService: CompanyService
  ) {
    super(formBuilder, toastyService);
    this.loadFieds();
    this.dispensedSuitability$.subscribe((value) => {
      this.dispensedSuitability = value;
    });
  }

  fieldAvailableForAgent(
    agent: AgentRegister,
    field: string,
    section: string
  ): boolean {
    if (agent) {
      if (agent.personType === PersonType.Fisica) {
        const agentEntityType: string = agent.entitiesType[0];

        return this.personSectionFields(
          section,
          agentEntityType,
          agent.services
        ).includes(field);
      }
    }
  }

  stepAvailableForAgent(agent: AgentRegister, nextStep): boolean {
    const types = [
      'SuitabilitySituation',
      'SuitabilityProduct',
      'SuitabilityKnowledge',
      'AuthorizationsDeclarations',
      'Bank',
      'Partner',
      'EconomicGroup',
      'Representatives',
      'AssetPortfolio'
    ];

    switch (nextStep) {
      case types.some((type) => nextStep === type):
      case WizardCompanyEnum.Bank:
      case WizardCompanyEnum.Partner:
      case WizardCompanyEnum.Data:
        return this.checkingAccessStepOfAgent(agent);
      default:
        return false;
    }
  }

  checkingAccessStepOfAgent(agent: AgentRegister): boolean {
    if (agent.entitiesType.includes(RegistratinEntityType.Cotista)) {
      return true;
    }

    return false;
  }

  getAllRoutesFromAgent(agent: AgentRegister): any {
    const routes = agent.entitiesType.map(
      (agentType) => AgentRoutes[agentType]
    );
    return [...new Set([].concat(...routes))];
  }

  getNextStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    let routes = this.getAllRoutesFromAgent(agent);
    routes = routes.filter((route) =>
      this.skipCompanySteps(route, new AgentRegister(agent))
    );
    const nextRouteIndex = routes.indexOf(actualRoute) + 1;
    return routes[nextRouteIndex];
  }

  getPreviousStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    let routes = this.getAllRoutesFromAgent(agent);
    routes = routes.filter((rt) => this.skipCompanySteps(rt, agent));
    const previousRouteIndex = routes.indexOf(actualRoute) - 1;
    return routes[previousRouteIndex];
  }

  getCurrentStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    const routes = this.getAllRoutesFromAgent(agent);
    const currentRouteIndex = routes.indexOf(actualRoute);
    if (currentRouteIndex === -1) {
      const firstRoute = AgentRoutes[AgentType.Shareholder][0];
      return firstRoute;
    }
    return routes[currentRouteIndex];
  }

  skipCompanySteps(step: string, agent: AgentRegister) {
    const skipPartner = step !== WizardCompanyEnum.Partner;
    const skipFiscalData = step !== WizardCompanyEnum.FiscalData;
    const skipAssetPortfolio = step !== WizardCompanyEnum.AssetPortfolio;
    const skipFinancialInformation =
      step !== WizardCompanyEnum.FinancialInformation;
    const skipSuitability = step !== WizardCompanyEnum.Suitability;
    const dispensedSuitability = skipSuitability
      ? true
      : !this.dispensedSuitability;
    const skipAuthorizations = step !== WizardCompanyEnum.Authorizations;
    const skipPatrimonialSituation = step !== WizardCompanyEnum.Patrimonial;
    const isInvestmentFund = agent.personType === PersonType.Juridica;
    const isPJ = agent.personType === PersonType.PJInvestidor;
    const isIssuer = agent.personType === PersonType.Emissor;
    const skipBeneficiaries = step !== WizardCompanyEnum.Beneficiaries;
    const skipContract = step !== WizardCompanyEnum.Contract;
    const skipPaymentAccountOpening =
      step !== WizardCompanyEnum.PaymentAccountOpening;

    const isEscrow =
      agent.isEscrowPartesRelacionadas() || agent.isEscrowTitular();

    const isBancoLiquidante =
      agent.isBancoLiquidantePartesRelacionadas() ||
      agent.isBancoLiquidanteTitular();

    const isfundo = agent.isFundo();

    if (isIssuer) {
      return (
        skipAssetPortfolio &&
        skipSuitability &&
        skipPatrimonialSituation &&
        skipAuthorizations &&
        skipBeneficiaries &&
        skipContract &&
        skipPaymentAccountOpening
      );
    }

    if (isPJ) {
      if (
        (agent.isEscrowTitular() || agent.isBancoLiquidanteTitular()) &&
        isfundo
      ) {
        return true;
      }

      if ((isEscrow || isBancoLiquidante) && !isfundo) {
        if (
          agent.isEscrowPartesRelacionadas() ||
          agent.isBancoLiquidantePartesRelacionadas()
        ) {
          return (
            skipBeneficiaries &&
            skipSuitability &&
            skipFinancialInformation &&
            skipAssetPortfolio &&
            skipContract &&
            skipPaymentAccountOpening
          );
        }
        return (
          skipSuitability && skipFinancialInformation && skipAssetPortfolio
        );
      }
    }

    if (isInvestmentFund) {
      return (
        skipPartner &&
        skipFiscalData &&
        skipAssetPortfolio &&
        skipFinancialInformation &&
        skipSuitability &&
        skipPatrimonialSituation &&
        skipBeneficiaries &&
        skipContract &&
        skipPaymentAccountOpening &&
        skipAuthorizations
      );
    }

    if (isPJ) {
      return (
        skipAssetPortfolio &&
        skipFinancialInformation &&
        dispensedSuitability &&
        skipBeneficiaries &&
        skipContract &&
        skipPaymentAccountOpening
      );
    }

    return true;
  }

  protected personSectionFields(
    section: string,
    agentEntityType: string,
    serviceRelationship?: ServiceRelationship[]
  ): string[] {
    const services = serviceRelationship
      ? serviceRelationship.map((sr) => sr.description)
      : [];
    switch (section) {
      case 'InvestorData':
        return this.personSectionInvestorDataFields(agentEntityType, services);
      case 'Contract':
        return this.personSectionContractFields(agentEntityType, services);
      case 'Address':
        break;
      case 'Profession':
        return this.personSectionProfesionFields(agentEntityType, services);
      case 'BankData':
        break;
      case 'AuthorizationsDeclarations':
        return this.personSectionAuthorizationsDataFields(
          agentEntityType,
          services
        );
      case 'Financial':
        return this.personSectionFinancialFields(agentEntityType, services);
      case 'SuitabilityProduct':
        break;
      case 'SuitabilitySituation':
        break;
      case 'SuitabilityKnowledge':
        break;
      case 'Confirmations':
        break;
      default:
        return [];
    }
  }

  private personSectionAuthorizationsDataFields(
    entityType: string,
    services?: (keyof typeof ServicesTypes)[]
  ): string[] {
    const fieldsToRemove: string[] = [];
    const fieldsToAdd: string[] = [];
    if (services) {
      if (
        services.includes(ServicesTypes.CONTA_ESCROW_TITULAR) ||
        services.includes(ServicesTypes.BANCO_LIQUIDANTE_TITULAR) ||
        services.includes(ServicesTypes.CONTA_ESCROW_PARTES_RELACIONADAS) ||
        services.includes(ServicesTypes.BANCO_LIQUIDANTE_PARTES_RELACIONADAS)
      ) {
        fieldsToRemove.push(
          'managedWallet',
          'consultantWallet',
          'inform',
          'extract'
        );
      }

      if (services.includes(ServicesTypes.FUNDOS)) {
        fieldsToAdd.push(
          'managedWallet',
          'consultantWallet',
          'inform',
          'extract'
        );
      }
    }

    switch (entityType) {
      case RegistratinEntityType.Cotista:
        fieldsToRemove.push('link');
        let personAuthorizations =
          this.personSectionAuthorizationsDataDefault.fields.slice();
        personAuthorizations = personAuthorizations.filter(
          (field) => !fieldsToRemove.includes(field)
        );
        personAuthorizations.push(
          ...fieldsToAdd.filter(
            (field) => !personAuthorizations.includes(field)
          )
        );
        return personAuthorizations;
      default:
        let defaultFields =
          this.personSectionAuthorizationsDataDefault.fields.slice();
        defaultFields = defaultFields.filter(
          (field) => !fieldsToRemove.includes(field)
        );
        defaultFields.push(
          ...fieldsToAdd.filter((field) => !defaultFields.includes(field))
        );
        return defaultFields;
    }
  }

  private personSectionInvestorDataFields(
    entityType: string,
    services?: (keyof typeof ServicesTypes)[]
  ): string[] {
    const fieldsToRemove: string[] = [];
    const fieldsToAdd: string[] = [];

    if (services) {
      if (services.includes(ServicesTypes.CONTA_ESCROW_TITULAR)) {
        fieldsToRemove.push('father', 'distributor', 'isFundoTransferido');
      }
      if (services.includes(ServicesTypes.CONTA_ESCROW_PARTES_RELACIONADAS)) {
        fieldsToRemove.push('father', 'distributor', 'isFundoTransferido');
      }
      if (services.includes(ServicesTypes.BANCO_LIQUIDANTE_TITULAR)) {
        fieldsToRemove.push('father', 'distributor', 'isFundoTransferido');
      }
      if (
        services.includes(ServicesTypes.BANCO_LIQUIDANTE_PARTES_RELACIONADAS)
      ) {
        fieldsToRemove.push('father', 'distributor', 'isFundoTransferido');
      }
      if (services.includes(ServicesTypes.FUNDOS)) {
        fieldsToAdd.push('father', 'distributor', 'isFundoTransferido');
      }
    }

    switch (entityType) {
      case RegistratinEntityType.Cotista:
        // Remover campos padrão para o tipo Cotista
        fieldsToRemove.push(
          'issuingState',
          'sheetNumber',
          'termNumber',
          'bookNumber',
          'documentImage'
        );
        let personInvestorData =
          this.personSectionInvestorDataDefault.fields.slice();
        personInvestorData = personInvestorData.filter(
          (field) => !fieldsToRemove.includes(field)
        );
        personInvestorData.push(
          ...fieldsToAdd.filter((field) => !personInvestorData.includes(field))
        );
        return personInvestorData;
      default:
        let defaultFields =
          this.personSectionInvestorDataDefault.fields.slice();
        defaultFields = defaultFields.filter(
          (field) => !fieldsToRemove.includes(field)
        );
        defaultFields.push(
          ...fieldsToAdd.filter((field) => !defaultFields.includes(field))
        );
        return defaultFields;
    }
  }

  protected personSectionContractFields(
    entityType: string,
    services?: (keyof typeof ServicesTypes)[]
  ): string[] {
    const fieldsToRemove: string[] = [];
    const fieldsToAdd: string[] = [];

    if (services && services.includes(ServicesTypes.CONTA_ESCROW_TITULAR)) {
      fieldsToAdd.push('relationshipType');
      fieldsToRemove.push('distributionType');
    }
    if (services && services.includes(ServicesTypes.BANCO_LIQUIDANTE_TITULAR)) {
      fieldsToRemove.push('relationshipType');
      fieldsToAdd.push('distributionType');
    }

    let defaultFields = this.personSectionContractDefault.fields.slice();
    defaultFields = defaultFields.filter(
      (field) => !fieldsToRemove.includes(field)
    );
    defaultFields.push(
      ...fieldsToAdd.filter((field) => !defaultFields.includes(field))
    );

    return defaultFields;
  }

  private personSectionProfesionFields(
    entityType: string,
    services?: (keyof typeof ServicesTypes)[]
  ): string[] {
    const fieldsToRemove: string[] = [];
    const fieldsToAdd: string[] = [];

    if (services && services.includes(ServicesTypes.CONTA_ESCROW_TITULAR)) {
      fieldsToRemove.push('address');
    }
    if (services && services.includes(ServicesTypes.BANCO_LIQUIDANTE_TITULAR)) {
      fieldsToRemove.push('address');
    }
    if (
      services &&
      services.includes(ServicesTypes.BANCO_LIQUIDANTE_PARTES_RELACIONADAS)
    ) {
      fieldsToRemove.push('address');
    }
    if (
      services &&
      services.includes(ServicesTypes.CONTA_ESCROW_PARTES_RELACIONADAS)
    ) {
      fieldsToRemove.push('address');
    }
    if (services && services.includes(ServicesTypes.FUNDOS)) {
      fieldsToAdd.push('address');
    }

    let defaultFields = this.personSectionProfesionDataDefault.fields.slice();
    defaultFields = defaultFields.filter(
      (field) => !fieldsToRemove.includes(field)
    );
    defaultFields.push(
      ...fieldsToAdd.filter((field) => !defaultFields.includes(field))
    );

    return defaultFields;
  }

  private personSectionFinancialFields(
    entityType: string,
    services?: (keyof typeof ServicesTypes)[]
  ): string[] {
    const fieldsToRemove: string[] = [];
    const fieldsToAdd: string[] = [];

    if (services && services.includes(ServicesTypes.CONTA_ESCROW_TITULAR)) {
      fieldsToRemove.push('investorLevel');
      fieldsToRemove.push('equityTotal');
    }
    if (services && services.includes(ServicesTypes.BANCO_LIQUIDANTE_TITULAR)) {
      fieldsToRemove.push('investorLevel');
      fieldsToRemove.push('equityTotal');
    }
    if (
      services &&
      services.includes(ServicesTypes.BANCO_LIQUIDANTE_PARTES_RELACIONADAS)
    ) {
      fieldsToRemove.push('investorLevel');
      fieldsToRemove.push('equityTotal');
    }
    if (
      services &&
      services.includes(ServicesTypes.CONTA_ESCROW_PARTES_RELACIONADAS)
    ) {
      fieldsToRemove.push('investorLevel');
      fieldsToRemove.push('equityTotal');
    }
    if (services && services.includes(ServicesTypes.FUNDOS)) {
      fieldsToAdd.push('investorLevel');
      fieldsToAdd.push('equityTotal');
    }

    let defaultFields = this.personSectionFinancialDefault.fields.slice();
    defaultFields = defaultFields.filter(
      (field) => !fieldsToRemove.includes(field)
    );
    defaultFields.push(
      ...fieldsToAdd.filter((field) => !defaultFields.includes(field))
    );

    return defaultFields;
  }

  loadFieds() {
    this.personSectionAuthorizationsDataDefault = {
      section: 'AuthorizationsDeclarations',
      fields: [
        'transmissionByProdurer',
        'produrer',
        'isRepresentative',
        'isOrderSender',
        'signatureKind',
        'socialNumber',
        'name',
        'position',
        'ppe',
        'typePerson',
        'politicalExposedInformation',
        'residenceOutside',
        'country',
        'taxRegime',
        'managedWallet',
        'consultantWallet',
        'inform',
        'extract',
        'moreCitizenship',
        'citizenship',
        'noHaveTax',
        'document',
        'nationalityRenounced',
        'declaredNonResidentOption',
        'declaredNonResident'
      ]
    };

    this.personSectionInvestorDataDefault = {
      section: 'InvestorData',
      fields: [
        'socialNumber',
        'email',
        'fullName',
        'brithday',
        'gender',
        'nationality',
        'state',
        'natural',
        'mother',
        'father',
        'cellphone',
        'phone',
        'civilStatus',
        'spouse',
        'spouseDocument',
        'maritalRegime',
        'documentType',
        'documentNumber',
        'issuingOrgan',
        'issuingDate',
        'personDocument',
        'sheetNumber',
        'termNumber',
        'bookNumber',
        'issuingState',
        'address',
        'files',
        'fillDate',
        'city',
        'personDocument',
        'documentImage'
      ]
    };

    this.personSectionContractDefault = {
      section: 'Contract',
      fields: [
        'relationshipType',
        'issueNumber',
        'issueSeries',
        'issueDate',
        'assets',
        'distributionType',
        'estimatedValue'
      ]
    };

    this.personSectionProfesionDataDefault = {
      section: 'Profession',
      fields: [
        'jobName',
        'company',
        'companyDocument',
        'notWorking',
        'address.zipCode',
        'address.street',
        'address.number',
        'address.hasNoNumber',
        'address.complement',
        'address.district',
        'address.state',
        'address.city',
        'address.correspondenceAddress'
      ]
    };

    this.personSectionFinancialDefault = {
      section: 'Financial',
      fields: ['investorLevel', 'equityTotal']
    };
  }
}

export class RegisterSection {
  section: string;
  fields: string[];
}
